// ABOUTME: Admin component for configuring developmental behavior averages used in analytics
// ABOUTME: Provides a table interface for editing per-day averages by age group and behavior type

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { adminAPI } from '../services/api';
import { DevelopmentalAverageDto, BehaviorType } from '../types/api';

interface AgeGroup {
  start: number;
  end: number;
  label: string;
}

const AGE_GROUPS: AgeGroup[] = [
  { start: 0, end: 3, label: 'Under 4' },
  { start: 4, end: 5, label: '4-5 Years' },
  { start: 6, end: 7, label: '6-7 Years' },
  { start: 8, end: 9, label: '8-9 Years' },
  { start: 10, end: 999, label: 'Over 9' }
];

const BEHAVIOR_TYPES = [
  { type: BehaviorType.Star, label: 'Stars' },
  { type: BehaviorType.Triangle, label: 'Triangles' },
  { type: BehaviorType.Square, label: 'Storms' },
  { type: BehaviorType.FlipYourCoin, label: 'Reset' },
  { type: BehaviorType.MakeItRight, label: 'MakeItRight' },
  { type: BehaviorType.OneOnOneTime, label: 'Let Go' }
];

const AdminDevelopmentalAverages: React.FC = () => {
  const [averages, setAverages] = useState<DevelopmentalAverageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create a lookup table for easy access
  const averageLookup = averages.reduce((lookup, avg) => {
    const key = `${avg.ageGroupStart}-${avg.ageGroupEnd}-${avg.behaviorType}`;
    lookup[key] = avg.averagePerDay;
    return lookup;
  }, {} as { [key: string]: number | null });

  useEffect(() => {
    loadAverages();
  }, []);

  const loadAverages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminAPI.getDevelopmentalAveragesAdmin();
      setAverages(data);
    } catch (err) {
      setError('Failed to load developmental averages');
      console.error('Error loading developmental averages:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateValue = (ageStart: number, ageEnd: number, behaviorType: BehaviorType, value: string) => {
    const key = `${ageStart}-${ageEnd}-${behaviorType}`;
    const numericValue = value === '' ? null : parseFloat(value);
    
    // Validate numeric input
    if (value !== '' && (isNaN(numericValue!) || numericValue! < 0)) {
      return; // Ignore invalid input
    }

    // Update the lookup for immediate UI feedback
    averageLookup[key] = numericValue;
    
    // Update the averages array
    const existingIndex = averages.findIndex(
      avg => avg.ageGroupStart === ageStart && avg.ageGroupEnd === ageEnd && avg.behaviorType === behaviorType
    );

    if (existingIndex >= 0) {
      // Update existing
      const updated = [...averages];
      updated[existingIndex] = {
        ...updated[existingIndex],
        averagePerDay: numericValue
      };
      setAverages(updated);
    } else {
      // Add new
      const newAverage: DevelopmentalAverageDto = {
        id: 0, // Will be set by backend
        ageGroupStart: ageStart,
        ageGroupEnd: ageEnd,
        behaviorType: behaviorType,
        averagePerDay: numericValue
      };
      setAverages(prev => [...prev, newAverage]);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Prepare all 30 possible combinations (5 age groups × 6 behavior types)
      const allCombinations: DevelopmentalAverageDto[] = [];
      
      AGE_GROUPS.forEach(ageGroup => {
        BEHAVIOR_TYPES.forEach(behaviorType => {
          const key = `${ageGroup.start}-${ageGroup.end}-${behaviorType.type}`;
          const value = averageLookup[key] || null;
          
          allCombinations.push({
            id: 0, // Backend will handle
            ageGroupStart: ageGroup.start,
            ageGroupEnd: ageGroup.end,
            behaviorType: behaviorType.type,
            averagePerDay: value
          });
        });
      });

      const updatedAverages = await adminAPI.updateDevelopmentalAverages(allCombinations);
      setAverages(updatedAverages);
      setSuccessMessage('Changes saved successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save changes');
      console.error('Error saving developmental averages:', err);
    } finally {
      setSaving(false);
    }
  };

  const getValue = (ageStart: number, ageEnd: number, behaviorType: BehaviorType): string => {
    const key = `${ageStart}-${ageEnd}-${behaviorType}`;
    const value = averageLookup[key];
    return value === null || value === undefined ? '' : value.toString();
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Developmental Averages
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Configure average behaviors per day by age group and behavior type. 
          Used for analytics comparison features. Leave blank to disable comparison for that behavior type.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Behavior Type</TableCell>
                {AGE_GROUPS.map(ageGroup => (
                  <TableCell key={`${ageGroup.start}-${ageGroup.end}`} align="center">
                    {ageGroup.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {BEHAVIOR_TYPES.map(behaviorType => (
                <TableRow key={behaviorType.type}>
                  <TableCell component="th" scope="row">
                    <strong>{behaviorType.label}</strong>
                  </TableCell>
                  {AGE_GROUPS.map(ageGroup => (
                    <TableCell key={`${ageGroup.start}-${ageGroup.end}-${behaviorType.type}`} align="center">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{
                          min: 0,
                          step: 0.1,
                          style: { textAlign: 'center' }
                        }}
                        value={getValue(ageGroup.start, ageGroup.end, behaviorType.type)}
                        onChange={(e) => updateValue(ageGroup.start, ageGroup.end, behaviorType.type, e.target.value)}
                        placeholder="—"
                        sx={{ width: 80 }}
                        variant="outlined"
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdminDevelopmentalAverages;